import { isTestAddress, MAX_REPLY } from "./support.utils";

/*
 * Help desk "Schedule send" and "New email" (owner 2026-11-03: "Add ability to send or schedule send emails in the
 * support"). Times are picked in the browser's own time zone and sent as an ISO time with its offset; the server
 * stores UTC and refuses the past (1 minute slack) and more than a year ahead, like these checks.
 */

export const SCHEDULE_SLACK_MS = 60 * 1000;
export const SCHEDULE_MAX_AHEAD_DAYS = 366;
export const MAX_RECIPIENTS = 10;
export const MAX_SUBJECT = 300;

export type QuickPick = {
  key: string;
  label: string;
  detail: string;
  at: Date;
};

const at = (base: Date, addDays: number, hour: number) => {
  const d = new Date(base);
  d.setDate(d.getDate() + addDays);
  d.setHours(hour, 0, 0, 0);
  return d;
};

/** The quick choices of the Schedule send menu: this afternoon (before 1 PM), tomorrow 8 AM, Monday 8 AM. */
export const quickPicks = (now: Date): QuickPick[] => {
  const picks: QuickPick[] = [];
  if (now.getHours() < 13)
    picks.push({
      key: "afternoon",
      label: "This afternoon",
      detail: formatSendTime(at(now, 0, 13), now, undefined, true),
      at: at(now, 0, 13),
    });
  const tomorrow = at(now, 1, 8);
  picks.push({
    key: "tomorrow",
    label: "Tomorrow morning",
    detail: formatSendTime(tomorrow, now, undefined, true),
    at: tomorrow,
  });
  // The next Monday after today; skipped when that is tomorrow (already listed).
  const toMonday = (8 - now.getDay()) % 7 || 7;
  if (toMonday !== 1) {
    const monday = at(now, toMonday, 8);
    picks.push({
      key: "monday",
      label: "Monday morning",
      detail: formatSendTime(monday, now, undefined, true),
      at: monday,
    });
  }
  return picks;
};

/** "Tue, Nov 4, 8:00 AM" (+ the year when it isn't this year), in the given zone or the browser's. */
export const formatSendTime = (
  when: Date | string,
  now: Date = new Date(),
  timeZone?: string | null,
  short = false,
): string => {
  const d = typeof when === "string" ? new Date(when) : when;
  if (Number.isNaN(d.getTime())) return "";
  const zone = timeZone || undefined;
  const year = (x: Date) =>
    Number(
      new Intl.DateTimeFormat("en-US", {
        year: "numeric",
        timeZone: zone,
      }).format(x),
    );
  const options: Intl.DateTimeFormatOptions = {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZone: zone,
  };
  if (!short && year(d) !== year(now)) options.year = "numeric";
  return d.toLocaleString("en-US", options).replace(/ /g, " ");
};

/** The browser's IANA time zone ("America/Vancouver"), or "" when the browser doesn't say. */
export const browserTimeZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch {
    return "";
  }
};

const pad = (n: number) => String(n).padStart(2, "0");

/** A date for an <input type="datetime-local"> (local time, minutes). */
export const toLocalInput = (d: Date) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;

/** The value of a datetime-local box as a local time, or null. */
export const fromLocalInput = (value: string): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value.trim());
  if (!m) return null;
  const d = new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], 0, 0);
  return Number.isNaN(d.getTime()) || d.getMonth() !== +m[2] - 1 ? null : d;
};

/** Same rules as the server; "" = fine. */
export const scheduleProblem = (when: Date | null, now: Date = new Date()) => {
  if (!when) return "Pick when the email should go out.";
  if (when.getTime() < now.getTime() - SCHEDULE_SLACK_MS)
    return "Pick a time in the future.";
  if (when.getTime() > now.getTime() + SCHEDULE_MAX_AHEAD_DAYS * 86400000)
    return "You can schedule an email up to one year ahead.";
  return "";
};

/** The To box: addresses split by commas, semicolons, spaces or lines; lower case, no repeats. */
export const parseRecipients = (
  text: string,
): { addresses: string[]; invalid: string[] } => {
  const addresses: string[] = [];
  const invalid: string[] = [];
  for (const part of text.split(/[\s,;]+/)) {
    const a = part.trim().replace(/^<|>$/g, "").toLowerCase();
    if (!a) continue;
    if (!isTestAddress(a)) invalid.push(part.trim());
    else if (!addresses.includes(a)) addresses.push(a);
  }
  return { addresses, invalid };
};

/** What stops "New email" from being sent; "" = fine. */
export const newEmailProblem = (form: {
  to: string;
  subject: string;
  text: string;
}) => {
  const { addresses, invalid } = parseRecipients(form.to);
  if (invalid.length) return `“${invalid[0]}” isn't an email address.`;
  if (!addresses.length) return "Add at least one email address.";
  if (addresses.length > MAX_RECIPIENTS)
    return `You can send to at most ${MAX_RECIPIENTS} addresses at once.`;
  if (!form.subject.trim()) return "Write a subject.";
  if (form.subject.trim().length > MAX_SUBJECT)
    return `The subject is too long (max ${MAX_SUBJECT} characters).`;
  if (!form.text.trim()) return "Write the email first.";
  if (form.text.length > MAX_REPLY)
    return `The email is too long (max ${MAX_REPLY.toLocaleString(
      "en-US",
    )} characters).`;
  return "";
};

/** The case list's small marker: "Scheduled · Tue, Nov 4, 8:00 AM" / "Couldn't send", or "". */
export const scheduledMarker = (
  row: { scheduledFor?: string | null; scheduledStatus?: string | null },
  now: Date = new Date(),
): { text: string; failed: boolean } | null => {
  if (!row.scheduledStatus) return null;
  if (row.scheduledStatus === "failed")
    return { text: "Couldn't send", failed: true };
  if (row.scheduledStatus === "sending")
    return { text: "Sending…", failed: false };
  return {
    text: `Scheduled · ${
      row.scheduledFor ? formatSendTime(row.scheduledFor, now) : ""
    }`.replace(/ · $/, ""),
    failed: false,
  };
};

/** The toast after "New email". */
export const newEmailResultText = (
  results: { email: string; state: string; error?: string | null }[],
  sendAt?: string | null,
): { text: string; ok: boolean } => {
  const failed = results.filter((r) => r.state === "failed");
  const done = results.length - failed.length;
  const who = (n: number) => (n === 1 ? "1 person" : `${n} people`);
  if (failed.length === 0)
    return {
      ok: true,
      text: sendAt
        ? `Scheduled for ${formatSendTime(sendAt)} to ${
            results.length === 1 ? results[0].email : who(results.length)
          }.`
        : `Sent to ${
            results.length === 1 ? results[0].email : who(results.length)
          }.`,
    };
  return {
    ok: false,
    text:
      (done ? `Sent to ${who(done)}. ` : "") +
      `Not sent to ${failed.map((f) => f.email).join(", ")}: ${
        failed[0].error || "please try again"
      }. It's saved as a draft on ${
        failed.length === 1 ? "its case" : "their cases"
      }.`,
  };
};

// ---------- Cc / Bcc (owner 2026-11-03: "Can you add CC and BCC for scheduled send?") ----------

export const MAX_COPIES = 10;

/**
 * The Cc / Bcc boxes, checked like the server: each address valid, max 10 each; an address already in To (or Cc,
 * for Bcc) is dropped so nobody gets two copies. problem = "" when fine.
 */
export const cleanCopies = (
  to: string[],
  ccText: string,
  bccText: string,
): { cc: string[]; bcc: string[]; problem: string } => {
  const taken = new Set(to.map((a) => a.toLowerCase()));
  const out = { cc: [] as string[], bcc: [] as string[] };
  for (const [key, text, label] of [
    ["cc", ccText, "Cc"],
    ["bcc", bccText, "Bcc"],
  ] as const) {
    const { addresses, invalid } = parseRecipients(text);
    if (invalid.length)
      return {
        ...out,
        problem: `${label}: “${invalid[0]}” isn't an email address.`,
      };
    if (addresses.length > MAX_COPIES)
      return {
        ...out,
        problem: `You can add at most ${MAX_COPIES} ${label} addresses.`,
      };
    for (const a of addresses)
      if (!taken.has(a)) {
        taken.add(a);
        out[key].push(a);
      }
  }
  return { ...out, problem: "" };
};

/** New email to several people: Cc / Bcc get a copy of each email. "" when it doesn't apply. */
export const copiesNote = (toCount: number, cc: string[], bcc: string[]) =>
  toCount > 1 && cc.length + bcc.length > 0
    ? `Cc and Bcc get a copy of each of the ${toCount} emails.`
    : "";

/** "to a@b.com · cc c@d.com · bcc e@f.com" (the scheduled card). */
export const recipientsLine = (
  to: string,
  cc?: string[] | null,
  bcc?: string[] | null,
) =>
  [
    `to ${to}`,
    cc?.length ? `cc ${cc.join(", ")}` : "",
    bcc?.length ? `bcc ${bcc.join(", ")}` : "",
  ]
    .filter(Boolean)
    .join(" · ");
